import * as $ from "jquery";
import * as moment from "moment";
import * as localisationHelper from "localisationHelper";

// Types
import Moment = moment.Moment;

// Models
import { DatePickerBindingConfiguration, DatePickerLibraryEvents } from "Content/ts/Models/Shared/DateTime/datePickerConfiguration";

// Services
import { DateTimeFormatterService } from "Content/ts/Shared/Controls/DateTime/Services/dateTimeFormatterService";

export const Deps = {
    LocalisationHelper: localisationHelper
};

export module DatePickerServices {
    const LocalisedMomentDateFormat = DateTimeFormatterService.LocalisedMomentShortDateFormat;

    export function DatePickerOnLoad(element: HTMLElement, allBindings: DatePickerBindingConfiguration, value: KnockoutComputed<Moment>): void {
        const today = moment().startOf("day"),
            dateTimePicker = $(element).data("Zebra_DatePicker") as DatePickerLibraryEvents;

        // Purpose  :   Will bind the value to the component text box at binding load if a value has been provided.
        if (value()) {
            let currentValue = value().format(LocalisedMomentDateFormat);

            if (!value().isValid() || (CurrentFutureDatesOnly(allBindings) && value().isBefore(today))) {
                currentValue = moment().startOf("day").format(LocalisedMomentDateFormat);
            }

            dateTimePicker.set_date(currentValue);
        }
    };

    // Important:   Cannot be done via a standard subscribe due to the way Zebra works. When the datepicker is initialised the Knockout context 
    //              is mostly destroyed so you lose some visibility on the HTML element. Due to this a couple of change events are bound to the 
    //              object so these are detected without the user having to unfocus from the element when dealing with a form.
    export function ConfigureKeyPressEvents(element: HTMLElement, allBindings: DatePickerBindingConfiguration, valueToUpdate: KnockoutComputed<Moment>): void {
        const dateTimePicker = $(element).data("Zebra_DatePicker") as DatePickerLibraryEvents;

        $(element)
            .on("blur", function () {
                const currentValue = $(this).val() as string,
                    observableValue = valueToUpdate() as Moment;

                if ((!observableValue && currentValue !== "") || (observableValue && currentValue != observableValue.format(DateTimeFormatterService.LocalisedMomentShortDateFormat))) {
                    currentValue ?
                        UpdateDate(element, allBindings, currentValue, valueToUpdate) :
                        dateTimePicker.clear_date();
                }
            })
            .on("keydown", function (event: JQueryEventObject) {
                const currentValue = $(this).val() as string,
                    observableValue = valueToUpdate() as Moment;

                // Important:   'keyCode' and 'which' have been deprecated across all browsers. Said codes didn't have a consistent implementation
                //              across all browsers so we've gone for this implementation as it seems to be more stable.
                // Sources  :   https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode
                //              https://stackoverflow.com/questions/56771542/how-to-call-the-jquery-click-method-when-pressing-enter-on-the-keyboard
                if (event.key == "Enter") {
                    if ((!observableValue && currentValue !== "") || (observableValue && currentValue != observableValue.format(DateTimeFormatterService.LocalisedMomentShortDateFormat))) {
                        currentValue ?
                            UpdateDate(element, allBindings, currentValue, valueToUpdate) :
                            dateTimePicker.clear_date();
                    }
                }
            });

        $(element);
    };

    export function ConfigurePickerDirection(allBindings: DatePickerBindingConfiguration): string | number | boolean | Array<string | number | boolean> {
        // Example  :   '["01/02/2020", "01/06/2020"]' - Starts on 1 February 2020 & Ends on 1 June 2020
        if (allBindings.configuration.BetweenDates) {
            return allBindings.configuration.BetweenDates;
        }

        if (allBindings.configuration.StartDate) {
            return [allBindings.configuration.StartDate.format(LocalisedMomentDateFormat), false];
        }

        if (allBindings.configuration.EndDate) {
            return [false, allBindings.configuration.EndDate.format(LocalisedMomentDateFormat)];
        }

        // Purpose  :   Default Behaviour - Calendar has no restrictions
        return 0;
    };

    function UpdateDate(element: HTMLElement, allBindings: DatePickerBindingConfiguration, value: any, valueToUpdate: KnockoutComputed<Moment>): void {
        const dateTimePicker = $(element).data("Zebra_DatePicker") as DatePickerLibraryEvents;

        let date = moment(value, LocalisedMomentDateFormat);

        if (allBindings.configuration.BetweenDates) {
            const endDate = moment(allBindings.configuration.BetweenDates[1], LocalisedMomentDateFormat),
                startDate = moment(allBindings.configuration.BetweenDates[0], LocalisedMomentDateFormat);

            if (!date.isValid() || !date.isSameOrAfter(startDate) || !date.isSameOrBefore(endDate)) {
                date = startDate;
            }
        } else if (CurrentFutureDatesOnly(allBindings)) {
            if (!date.isValid() || date.isBefore(allBindings.configuration.StartDate)) {
                date = allBindings.configuration.StartDate;
            }
        }

        valueToUpdate(date);

        if (date) {
            dateTimePicker.set_date(date.format(LocalisedMomentDateFormat));
        } else {
            dateTimePicker.clear_date();
        }
    };

    function CurrentFutureDatesOnly(allBindings: DatePickerBindingConfiguration): Array<string | boolean> | boolean {
        if (allBindings.configuration.StartDate) {
            const today = moment().startOf("day");
            return allBindings.configuration.StartDate.startOf("day").isSame(today);
        }

        return false;
    };
}
